import React from 'react'
import { Helmet } from 'react-helmet'
import { IonText, IonGrid, IonRippleEffect, IonCol, IonSpinner, IonRow, IonButton } from '@ionic/react'

import { AudioPlayerContainer as Container, PlayerStatus } from 'mytours-read-common'
import { Howl } from 'howler'
import { getUAParser } from '../../../../services/uaParser'

import {
	setCurrentSound,
	getCurrentSound,
	onBack15,
	onForward30,
	sendAnalyticsEvent,
	getAllSounds,
} from '../../../audio/soundController'
import AudioTranscript from '../../../audio/components/AudioTranscript'
import { sendEvent, EventActionsEnum } from '../../../logging/analytics'
import { getDirMode, formatNumberStyle, waitForSomething } from '../../../platform'

import Button from '../../../common/components/Button'
import Modal from '../../../common/components/modal'
import strings from '../../../../strings'
import { cleanText } from '../../../common/functions'
import { FileRec } from 'mytours-read-api-typescript'
import { ImageSizePreset, ImageSizePresetMap } from '../../../../common_lib/types'
import { BaseUrlEnum, fetchAudio, generateFilePath, generateImageUrl } from '../../../../common_lib/utils'
import { ionHistory } from 'mobile_web/modules/navigation'
import { updateTranscriptionHash } from '../../functions'

import '../styles/sectionMediaAudio.less'
import { formatPlayPauseLabel } from 'mobile_web/modules/audio/functions'
import { getSectionsQueryParams } from 'mobile_web/modules/navigation/queryParams'

interface HelmetData {
	title: string
	transcription: string
	file: FileRec
	imageUrl: string
	imageHeight: number | string
}

interface State {
	showTranscriptModal: boolean
}

type Props = Container.Props & Container.Actions

class SectionMediaAudio extends React.Component<Props, State> {
	state = {
		showTranscriptModal: false,
	}

	private positionInterval: NodeJS.Timeout | number
	private progressBreakpoints = {
		10: false,
		25: false,
		50: false,
		75: false,
	}
	private playButtonClicked = false
	private hash = ''
	private unListen: () => void

	componentDidMount = () => {
		const {
			container,
			audioPreview,
			coverImages,
			startTimes,
			fullScreenEnabled,
			collectionId,
			screen,
			slideshowEnabled,
		} = this.props

		this.props.loadAudioPreview(container, audioPreview, {
			coverImages,
			startTimes,
			fullScreenEnabled,
			collectionId,
			screen,
			slideshowEnabled,
		})
		this.createSound()

		this.unListen = ionHistory.listen(({ hash }) => {
			if (hash !== this.hash) {
				// TODO: this is temp fix: add global handle for hash changes
				if (hash === '' && this.hash === '#transcript' && this.state.showTranscriptModal) {
					this.toggleTranscriptModal(false)
				}

				this.hash = hash
			}
		})

		this.openAudioPlayerDependsOnQueryParams()
	}

	componentWillUnmount = () => {
		this.unListen()
	}

	private openAudioPlayerDependsOnQueryParams = () => {
		const { storySectionId, sectionItemId } = getSectionsQueryParams()

		if (!storySectionId || !sectionItemId) return

		const { audioPreview, container } = this.props
		const { file, id: audioId } = audioPreview
		const { id: fileId } = file

		if (sectionItemId !== audioId || storySectionId !== container?.id) return

		waitForSomething(() => getCurrentSound(audioId, container, fileId)).then(() => {
			// show audio bar to have access to fullscreen audio player
			this.props.setPlayerStatus(audioId, PlayerStatus.Pause, container, fileId)
		})
	}

	private createSound = async (): Promise<void> => {
		const { audioPreview, container } = this.props

		if (audioPreview?.file) {
			const { file, id: audioId, dont_transform: dontTransform } = audioPreview
			const { id: fileId, filename, region } = file

			const { base, path } = generateFilePath(String(fileId), BaseUrlEnum.BunnyUploads, {
				pathType: dontTransform ? 'original' : 'quality_7',
				region,
			})
			let src = base + path + filename
			const audioFormat = filename.split('.').reverse()[0]
			let isFirstStart = true

			// load mp3 files first when `quality_7`
			if (!dontTransform) {
				const res = await fetchAudio(src)
				if (res) {
					src = res
				}
			}

			// firefox doesn't support html5 playing from howler
			// https://github.com/goldfire/howler.js/issues/1411
			const browser = getUAParser().getBrowser()
			const html5 = browser.name !== 'Firefox'

			const sound = new Howl({
				html5,
				// TODO: Update this place once we move to m4a
				format: [dontTransform ? audioFormat : 'mp3'],
				src: src + '?method=howl',
				preload: 'metadata',
				onload: () => {
					this.props.audioLoaded(audioId, sound.duration(), container, fileId)
				},
				onloaderror: (_, error: unknown) => {
					this.props.loadAudioError(audioId, error, container, fileId)
				},
				onplay: () => {
					const { currentAudio, collection } = this.props

					if (isFirstStart) {
						sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioStart, { audioDuration: 0 })
					} else {
						sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioResume)
					}
					this.setPositionInterval()
					this.props.setPlayerStatus(audioId, PlayerStatus.Playing, container, fileId)
					isFirstStart = false
				},
				onpause: () => {
					const { currentAudio, collection } = this.props
					this.props.setPlayerStatus(audioId, PlayerStatus.Pause, container, fileId)
					sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioPause)
					this.removePositionInterval()
				},
				onstop: () => {
					this.removePositionInterval(true)
					this.props.setPlayerStatus(audioId, PlayerStatus.Stop, container, fileId)
					this.props.setCurrentTime(audioId, 0, container, fileId)
					// make play button clickable again
					this.playButtonClicked = false
				},
				onend: () => {
					const { currentAudio, collection } = this.props
					this.removePositionInterval(true)
					this.props.setAudioEnded(audioId, container, fileId)
					sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioComplete, {
						audioCurrentTime: -1,
						audioPercent: 100,
					})
					// make play button clickable again
					this.playButtonClicked = false
				},
			})

			setCurrentSound(audioId, container, fileId, sound)

			return
		}
	}

	onPlayPause = (event: React.MouseEvent<HTMLIonButtonElement>): void => {
		event.stopPropagation()
		// block double click on play button
		if (this.playButtonClicked) {
			return
		}

		this.playButtonClicked = true

		setTimeout(() => {
			this.playButtonClicked = false
		}, 500)

		const { audioPreview, currentAudio, container } = this.props

		const { playerStatus, audioTrack } = currentAudio || {}

		if (playerStatus === PlayerStatus.Loading && audioTrack?.audio) {
			return
		}

		const currentSound = getCurrentSound(audioPreview?.id, container, audioPreview?.file?.id)

		if (currentSound) {
			if (this.isCurrentAudio) {
				switch (playerStatus) {
					case PlayerStatus.Playing:
						currentSound.pause()
						return
					case PlayerStatus.Pause:
					case PlayerStatus.Ended:
						currentSound.play()
						return
				}
			} else {
				if (playerStatus === PlayerStatus.NotInit) {
					currentSound.play()
					return
				}
			}

			const sounds = getAllSounds()
			for (const sound of sounds) {
				sound.stop()
			}

			currentSound.play()
		}
	}

	onForward30 = (event: React.MouseEvent<HTMLIonButtonElement>): void => {
		const { setCurrentTime, currentAudio, collection, container } = this.props
		const {
			duration,
			audioTrack: { audio },
		} = currentAudio || {}

		event.stopPropagation()

		sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioSkipForward)
		onForward30(audio?.id, duration, container, audio?.file?.id, time => {
			setCurrentTime(audio?.id, time, container, audio?.file?.id)
		})
	}

	onBack15 = (event: React.MouseEvent<HTMLIonButtonElement>): void => {
		const { setCurrentTime, currentAudio, collection, container } = this.props
		const {
			audioTrack: { audio },
		} = currentAudio || {}

		event.stopPropagation()

		sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioSkipBackward)
		onBack15(audio?.id, container, audio?.file?.id, time => {
			setCurrentTime(audio?.id, time, container, audio?.file?.id)
		})
	}

	toggleTranscriptModal = (value?: React.MouseEvent | boolean): void => {
		if (typeof value !== 'boolean') {
			value.stopPropagation()
		}

		this.setState((prev: State) => {
			updateTranscriptionHash(value as unknown as boolean, prev.showTranscriptModal, () => {
				const { audioPreview, collection } = this.props

				sendEvent(EventActionsEnum.AudioViewTranscript, {
					audioTitle: audioPreview.name,
					collection: collection?.name,
					collectionId: collection?.id,
				})
			})

			return {
				showTranscriptModal: typeof value === 'boolean' ? value : !prev.showTranscriptModal,
			}
		})
	}

	get isCurrentAudio(): boolean {
		const { currentAudio, audioPreview, container: sectionContainer } = this.props

		const { audioTrack, container: currentAudioContainer } = currentAudio || {}

		const isCurrentAudio =
			(audioTrack?.audio &&
				audioPreview.id === audioTrack?.audio.id &&
				currentAudioContainer.id === sectionContainer.id &&
				currentAudioContainer.type === sectionContainer.type) ||
			false
		return isCurrentAudio
	}

	get isLoading(): boolean {
		const { currentAudio } = this.props
		const { playerStatus } = currentAudio || {}

		return playerStatus === PlayerStatus.Loading && this.isCurrentAudio
	}

	get isPlaying(): boolean {
		const { currentAudio } = this.props
		const { playerStatus } = currentAudio || {}

		return playerStatus === PlayerStatus.Playing && this.isCurrentAudio
	}

	renderTranscriptModal = (): JSX.Element => {
		const { audioPreview } = this.props
		const { showTranscriptModal } = this.state

		return (
			<Modal isOpen={showTranscriptModal} onDidDismiss={() => this.toggleTranscriptModal(false)}>
				<AudioTranscript transcript={audioPreview.transcription} onBack={this.toggleTranscriptModal} />
			</Modal>
		)
	}

	renderHelmet = ({ title, transcription, file, imageUrl, imageHeight }: HelmetData): JSX.Element => {
		const schema = {
			type: 'application/ld+json',
			innerHTML: JSON.stringify({
				'@type': 'AudioObject',
				caption: title,
				transcript: cleanText(transcription),
				contentSize: file && file.file_size,
				contentUrl: imageUrl,
				encodingFormat: file && file.content_type,
				height: imageHeight,
			}),
		}

		return <Helmet script={[schema]} />
	}

	renderAudioRow = (): JSX.Element => {
		const { audioPreview } = this.props

		const { title, transcription, thumbnail_media_item: thumbnailMediaItem, file } = audioPreview

		if (!file) {
			return null
		}

		// image
		let imageUrl: string | undefined
		if (thumbnailMediaItem?.file) {
			imageUrl = generateImageUrl(thumbnailMediaItem?.file, {
				imageSizePreset: ImageSizePreset.Grid,
			})
		}

		return (
			<>
				{this.renderHelmet({
					title,
					transcription,
					file,
					imageUrl,
					imageHeight: ImageSizePresetMap[ImageSizePreset.Grid].height,
				})}

				<IonRow
					id={`section-item-id-${audioPreview.id}`}
					className="audio-item ion-activatable"
					aria-label={formatPlayPauseLabel(this.isPlaying, title)}
					onClick={this.onPlayPause}
					role="button"
					data-cy="section-media-audio-button"
				>
					<IonButton expand="full" fill="clear">
						<IonGrid>
							<IonRow>
								<IonCol
									className="thumbnail"
									style={{
										backgroundImage: imageUrl ? `url(${imageUrl})` : null,
									}}
								>
									<IonRow dir="ltr" className="audio-controls">
										<i className="fal fa-headphones headphones" />
										{this.isLoading ? (
											<IonSpinner name="crescent" />
										) : (
											<i className={`fas fa-${this.isPlaying ? 'pause' : 'play'}`} />
										)}
									</IonRow>
								</IonCol>

								<IonRow className="content" dir={getDirMode()}>
									<IonText className="title">{title}</IonText>

									{/* transcript */}
									{!!transcription && (
										<Button
											fill="clear"
											ariaLabel={strings.transcript}
											onClick={this.toggleTranscriptModal}
											className="transcript"
										>
											<i className="fal fa-align-left" />
										</Button>
									)}
								</IonRow>
								<IonRippleEffect type="bounded" />
							</IonRow>
						</IonGrid>
					</IonButton>
				</IonRow>

				{this.renderTranscriptModal()}
			</>
		)
	}

	renderAudioBig = (): JSX.Element => {
		const { audioPreview, height } = this.props
		const { thumbnail_media_item: thumbnailMediaItem, title, transcription, file } = audioPreview

		const isHeightTypeNumber = typeof height === 'number'

		let imageHeight: number | string
		if (isHeightTypeNumber) {
			imageHeight = height < 480 ? height : 480
		} else {
			imageHeight = height
		}
		const thumbnail = thumbnailMediaItem?.file
		const imageWidth = thumbnail && isHeightTypeNumber && thumbnail.width * (Number(height) / thumbnail.height) // keep image aspect ratio

		const imageUrl: string | undefined =
			thumbnail &&
			generateImageUrl(thumbnail, {
				imageSizePreset: ImageSizePreset.Row,
			})

		return (
			<>
				{this.renderHelmet({
					title,
					transcription,
					file,
					imageUrl,
					imageHeight,
				})}
				<IonGrid className="audio-big">
					<IonRow
						className="cover-image"
						style={{
							backgroundImage: imageUrl ? `url(${imageUrl})` : 'none',
							width: formatNumberStyle(imageWidth) || formatNumberStyle(imageHeight) || '100%',
							height: formatNumberStyle(imageHeight),
						}}
					>
						{this.isCurrentAudio ? (
							<IonRow className="controls">
								<IonCol>
									<Button
										fill="clear"
										className="time-controls-btn media-action-click"
										mode="md"
										onClick={this.onBack15}
										ariaLabel={strings.accessibility_seek_backward}
									>
										<i className="fak fa-skip-back" aria-label={strings.accessibility_seek_backward} />
									</Button>
								</IonCol>
								<IonCol>
									{this.isLoading ? (
										<IonSpinner name="crescent" />
									) : (
										<Button
											fill="clear"
											className="media-action-click"
											onClick={this.onPlayPause}
											ariaLabel={formatPlayPauseLabel(this.isPlaying, title)}
											// `ariaLabel` won't change unless we remount
											key={formatPlayPauseLabel(this.isPlaying, title)}
										>
											<i className={`fas fa-${this.isPlaying ? 'pause' : 'play'}`} />
										</Button>
									)}
								</IonCol>
								<IonCol>
									<Button
										fill="clear"
										className="time-controls-btn media-action-click"
										mode="md"
										onClick={this.onForward30}
										ariaLabel={strings.accessibility_seek_forward}
									>
										<i className="fak fa-skip-forward" aria-label={strings.accessibility_seek_forward} />
									</Button>
								</IonCol>
							</IonRow>
						) : (
							<Button
								onClick={this.onPlayPause}
								ariaLabel={formatPlayPauseLabel(this.isPlaying, title)}
								className="controls controls-small media-action-click"
							>
								{this.isLoading ? (
									<IonSpinner name="crescent" />
								) : (
									<i className={`fas fa-${this.isPlaying ? 'pause' : 'play'}`} />
								)}
							</Button>
						)}
					</IonRow>

					{title && (
						<IonRow className="audio-title-row">
							<i className="fal fa-headphones" />
							<IonText className="title">{title}</IonText>
							{!!transcription && (
								<IonButton onClick={this.toggleTranscriptModal} className="transcript-button media-action-click">
									<i className="fal fa-align-left" />
								</IonButton>
							)}
						</IonRow>
					)}
				</IonGrid>
				{this.renderTranscriptModal()}
			</>
		)
	}

	render = () => {
		const { height } = this.props

		if (height !== undefined) {
			return this.renderAudioBig()
		}

		return this.renderAudioRow()
	}

	private sendAudioProgressEvents = () => {
		const { collection, container, currentAudio } = this.props
		const { audioTrack } = currentAudio || {}
		const { audio } = audioTrack || {}

		const sound = getCurrentSound(audio?.id, container, audio?.file?.id)

		if (!sound) {
			return
		}

		const duration = sound.duration()
		const currentTime = sound.seek()
		const percent = Math.round((100 * currentTime) / duration)

		if (percent >= 10 && percent < 25 && !this.progressBreakpoints[10]) {
			sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioProgress, { audioPercent: 10 })
			this.progressBreakpoints[10] = true
		} else if (percent >= 25 && percent < 50 && !this.progressBreakpoints[25]) {
			sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioProgress, { audioPercent: 25 })
			this.progressBreakpoints[25] = true
		} else if (percent >= 50 && percent < 75 && !this.progressBreakpoints[50]) {
			sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioProgress, { audioPercent: 50 })
			this.progressBreakpoints[50] = true
		} else if (percent >= 75 && percent < 100 && !this.progressBreakpoints[75]) {
			sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioProgress, { audioPercent: 75 })
			this.progressBreakpoints[75] = true
		}
	}

	private setPositionInterval = (): void => {
		this.removePositionInterval()

		this.positionInterval = setInterval(() => {
			const { container, audioPreview } = this.props

			const seconds = getCurrentSound(audioPreview.id, container, audioPreview.file?.id).seek()

			this.props.setCurrentTime(audioPreview.id, seconds, container, audioPreview.file?.id)
			this.sendAudioProgressEvents()
		}, 500)
	}

	private removePositionInterval = (resetBreakpoints?: boolean): void => {
		if (this.positionInterval) {
			clearInterval(this.positionInterval as number)
			if (resetBreakpoints) {
				this.progressBreakpoints = {
					10: false,
					25: false,
					50: false,
					75: false,
				}
			}
		}
	}
}

const SectionMediaAudioComponent = Container.default(SectionMediaAudio)
export default SectionMediaAudioComponent as any
